import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useDropzone } from "react-dropzone";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import secureLocalStorage from "react-secure-storage";
import fileContext from "../../context/fileContext";
import toast, { Toaster } from "react-hot-toast";
import useAuth from "../../stores/authStore";
import { useFileStore } from "../../store/fileStore";
import { useFolderStore } from "../../store/folderStore";
import { auth, storage } from "../../helper/firebaseClient";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { api } from "../../utils/axios-instance";
import { v4 as uuidv4 } from "uuid";
import handleTextExtraction from "../../helper/fileExtraction";
import { TextField } from "@mui/material";
// import Chrome from "@uiw/react-color-chrome";
// import { GithubPlacement } from "@uiw/react-color-github";
import { useStorageStore } from "../../store/storageStore";

const FileIcon = `${process.env.PUBLIC_URL}/assets/fileIcon.webp`;

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "light" ? "#3B82F6" : "#B3A9EB",
  },
}));

const UnifiedUpload = ({ value, dept, folderId, listFilesInFolder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("initial");
  const folderInputRef = useRef(null);

  // Shared state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [depts, setdepts] = useState([]);
  const [deptId, setDeptId] = useState("");
  const [selectedDeptIndex, setSelectedDeptIndex] = useState(null);
  const [deptColor, setDeptColor] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isClicked, setIsClicked] = useState(false);

  // File specific state
  const [droppedFiles, setDroppedFiles] = useState([]);

  // Folder specific state
  // const [hex, setHex] = useState("#EFEEDC");
  const [folderName, setFolderName] = useState("");
  const [folderFiles, setFolderFiles] = useState([]);
  const fetchFolders = useFolderStore((state) => state.fetchFolders);

  const adjustLocal = useStorageStore((state) => state.adjustLocal);

  const profileData = useAuth((state) => state.profileData);
  const orgId =
    profileData?.org || profileData?.org_id || profileData?.orgId || "";
  const role = profileData?.role_priv || "employee";
  const allowedDeptIds = Array.isArray(profileData?.dept_ids)
    ? profileData.dept_ids
    : [];
  const isEmployee = role === "employee";

  const notBelongMsg =
    "you doesn't belong to this department, try uploading to the departments which you belong";

  const canUseDept = (id) => !isEmployee || allowedDeptIds.includes(id);

  const { deptName } = useParams();
  const { updateFilesState, updateDepartmentFiles, setRefreshFolders } =
    useContext(fileContext);
  const triggerFileRefresh = useFileStore((state) => state.triggerFileRefresh);

  const handleDepartmentClick = (index, id, bg) => {
    if (uploadMode === "file" && !canUseDept(id)) {
      toast.error(notBelongMsg);
      return;
    }
    setDeptId(id);
    setSelectedDeptIndex(index);
    setDeptColor(bg || "#EFEEDC");
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,

    onDrop: (acceptedFiles) => {
      const hasFolder = acceptedFiles.some(
        (file) => file.webkitRelativePath && file.webkitRelativePath.length > 0
      );

      if (hasFolder) {
        setFolderFiles(acceptedFiles);
        setUploadMode("folder");
      } else {
        setDroppedFiles(acceptedFiles);
        setUploadMode("file");
      }
    },
  });

  const handleFolderUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFolderFiles(selectedFiles);
    if (selectedFiles.length > 0) {
      setUploadMode("folder");
    }
  };

  const handleFolderDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setFolderFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const openDialog = () => {
    setIsOpen(true);
    const departments =
      JSON.parse(secureLocalStorage.getItem("departments")) || [];

    const filtered = isEmployee
      ? departments.filter((d) => allowedDeptIds.includes(d.id))
      : departments;

    setdepts(filtered);

    if (isEmployee && filtered.length === 0) {
      toast.error("No departments assigned to you. Contact your admin.");
    }
  };

  const closeDialog = () => {
    setIsOpen(false);
    if (uploadMode === "file") {
      for (const file of droppedFiles) {
        handleTextExtraction(
          file,
          file.id,
          file.name,
          deptColor,
          profileData,
          false
        );
      }
    }
    setDroppedFiles([]);
    setFolderFiles([]);
    setUploadProgress(0);
    setIsClicked(false);
    setUploadMode("initial");
    setDeptId("");
    setSelectedDeptIndex(null);
    setFolderName("");
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const uploadFileTask = async (file, id, fileIndex = 0, totalFiles = 1) => {
    return new Promise((resolve, reject) => {
      if (!orgId) {
        return reject(new Error("ORG ID MISSING during upload"));
      }

      const path = `files/${orgId}/${id}`;
      const fileRef = ref(storage, path);
      const metadata = {
        customMetadata: {
          department_id: deptId,
          org_id: orgId,
        },
      };

      const uploadTask = uploadBytesResumable(fileRef, file, metadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          if (uploadMode === "file") {
            const safeTotal = Math.max(1, Number(totalFiles) || 1);
            const safeIndex = Math.max(0, Number(fileIndex) || 0);
            const overall = Math.min(
              100,
              Math.max(0, (safeIndex / safeTotal) * 100 + progress / safeTotal)
            );
            setUploadProgress(overall);
          } else {
            setUploadProgress((prev) =>
              Math.min(100, prev + progress / Math.max(1, totalFiles))
            );
          }
        },
        (error) => {
          console.error("File upload error:", error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            resolve(
              uploadMode === "folder"
                ? {
                    downloadURL,
                    name: file.name,
                    id,
                    size: file.size,
                    mimetype: file.type,
                  }
                : downloadURL
            );
          });
        }
      );
    });
  };

  const handleFinalFileUpload = async () => {
    if (!orgId) {
      toast.error("User not ready. Please wait...");
      return;
    }
    if (!canUseDept(deptId)) {
      toast.error(notBelongMsg);
      return;
    }

    // ── User personal quota block (≥ 98%) ───────────────────────────────
    // Checked first — before the org-level quota — because this is a
    // per-seat limit. isUserStorageBlocked() always reads the latest store.
    const { isUserStorageBlocked, checkQuota } = useStorageStore.getState();
    if (isUserStorageBlocked()) {
      toast.error(
        "Your personal storage is almost full (≥ 98%). Free up space before uploading.",
        { duration: 5000 }
      );
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    // ── Org quota pre-check ──────────────────────────────────────────────
    // Sum all files being uploaded, check against remaining org quota.
    // checkQuota is read from getState() — not from reactive hook —
    // so it always uses the latest store values without being a render dep.
    const totalIncomingBytes = droppedFiles.reduce(
      (sum, f) => sum + (f.size || 0),
      0
    );
    const { allowed, message } = checkQuota(totalIncomingBytes);
    if (!allowed) {
      toast.error(message);
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    setIsClicked(true);

    const addFile = async (fileId) => {
      console.log("ADDING");
      const token = await auth.currentUser.getIdToken();
      let body = { file_id: fileId, idToken: token };
      try {
        const response = await api.post(
          `/file/folder/addFile/${folderId}`,
          body
        );

        if (response) {
          toast.success("File added successfully.");
          closeDialog();
        }
      } catch (error) {
        console.log("error occurred while creating folder.", error);
        toast.error("Error adding the file.");
      }
    };

    try {
      for (let i = 0; i < droppedFiles.length; i++) {
        const file = droppedFiles[i];
        const newFileId = uuidv4();
        const downloadURL = await uploadFileTask(
          file,
          newFileId,
          i,
          droppedFiles.length
        );
        await handleFileIdRetrieval(file, file.name, downloadURL, newFileId);
        file["id"] = newFileId;
        if (dept) {
          await addFile(file["id"]);
        }
      }
      if (dept) listFilesInFolder(folderId);
      showSnackbar("Upload successful", "success");
    } catch (error) {
      console.error("Error occurred in file upload:", error);
      showSnackbar("Upload failed. Please try again.", "error");
    } finally {
      closeDialog();
    }
  };

  const handleFileIdRetrieval = async (
    file,
    desiredFileName,
    downloadURL,
    newFileId
  ) => {
    const token = await auth.currentUser.getIdToken();
    try {
      await api.post(`/file/addDepartment/${newFileId}`, {
        department_ids: [deptId],
        new: true,
        name: desiredFileName,
        downloadURL: downloadURL,
        idToken: token,
        metadata: {
          size: file.size,
          mimetype: file.type,
          lastModified: new Date().toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          }),
        },
      });

      adjustLocal(file.size);

      toast.success("File uploaded successfully");
      triggerFileRefresh();
      updateFilesState(value);
      if (deptName) {
        updateDepartmentFiles(deptName);
      }
    } catch (error) {
      console.log(error);
      toast.error("Error uploading file");
    }
  };

  const handleFinalFolderUpload = async () => {
    if (!deptId) {
      showSnackbar("Please select a department", "warning");
      return;
    }
    if (!folderName) {
      showSnackbar("Please enter folder name", "warning");
      return;
    }

    // ── User personal quota block (≥ 98%) ───────────────────────────────
    // Same guard as single-file upload — a folder upload is just many
    // files at once, so it should be blocked on the same condition.
    const { isUserStorageBlocked, checkQuota } = useStorageStore.getState();
    if (isUserStorageBlocked()) {
      toast.error(
        "Your personal storage is almost full (≥ 98%). Free up space before uploading.",
        { duration: 5000 }
      );
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    // ── Org quota pre-check ──────────────────────────────────────────────
    const totalIncomingBytes = folderFiles.reduce(
      (sum, f) => sum + (f.size || 0),
      0
    );
    const { allowed, message } = checkQuota(totalIncomingBytes);
    if (!allowed) {
      toast.error(message);
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    setIsClicked(true);
    setUploadProgress(0); // Reset progress before upload

    try {
      const totalFiles = folderFiles.length;
      const newFileIds = Array.from({ length: totalFiles }, () => uuidv4());

      const uploadResponses = await Promise.all(
        folderFiles.map((file, i) =>
          uploadFileTask(file, newFileIds[i], i, totalFiles)
        )
      );

      await updateFolderCollection(uploadResponses);
      showSnackbar("Folder upload successful", "success");
    } catch (error) {
      console.error("Error uploading folder:", error);
      showSnackbar("Folder upload failed. Please try again.", "error");
    } finally {
      closeDialog();
    }
  };

  const updateFolderCollection = async (uploadResponses) => {
    const token = await auth.currentUser.getIdToken();
    const filesData = uploadResponses.map((res) => ({
      id: res.id,
      name: res.name,
      department_ids: [deptId],
      download_url: res.downloadURL,
      profile_pic: profileData.profilePictureUrl,
      metadata: {
        size: res.size,
        mimetype: res.mimetype,
        lastModified: new Date().toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
      },
    }));

    try {
      await api.post(`/file/folder/upload/`, {
        department_id: deptId,
        folderName: folderName,
        idToken: token,
        metadata: {},
        files: filesData,
      });

      // ── Optimistic sidebar update (entire folder in one delta) ─────────
      const totalBytes = uploadResponses.reduce(
        (sum, res) => sum + (res.size || 0),
        0
      );
      adjustLocal(totalBytes);
      // ──────────────────────────────────────────────────────────────────

      toast.success("Folder uploaded successfully");
      updateFilesState(value);
      if (deptName) {
        updateDepartmentFiles(deptName);
      }
      await fetchFolders();
    } catch (error) {
      console.log(error);
      toast.error("Error uploading folder");
    }
  };

  // V1: file uploads only. V2: restore ternary → uploadMode === "file" ? handleFinalFileUpload : handleFinalFolderUpload
  // const handleFinalUpload = handleFinalFileUpload;
  const handleFinalUpload =
    uploadMode === "file" ? handleFinalFileUpload : handleFinalFolderUpload;

  // V1: folder conditions removed. V2: restore full ternary below
  const isUploadDisabled =
    uploadMode === "file"
      ? !droppedFiles.length || !deptId || uploadProgress > 0 || isClicked
      : !folderFiles.length ||
        !deptId ||
        !folderName ||
        uploadProgress > 0 ||
        isClicked;
  // const isUploadDisabled =
  //   !droppedFiles.length || !deptId || uploadProgress > 0 || isClicked;

  function formatFileSize(sizeInBytes) {
    const units = ["B", "KB", "MB", "GB"];
    let size = sizeInBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return size.toFixed(2) + " " + units[unitIndex];
  }

  useEffect(() => {
    if (dept) {
      setDeptId(dept);
    }
  }, [dept]);

  return (
    <div className="">
      <Toaster reverseOrder={false} position="bottom-left" />
      <button
        onClick={openDialog}
        className="py-1 px-4 rounded-md border bg-blue-700 hover:bg-blue-500 text-white"
      >
        Upload
      </button>

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: "16px", minWidth: "40vw" },
        }}
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-md">Upload</h4>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: "#F7F8FA",
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
          }}
        >
          {uploadMode === "initial" && (
            <div className="my-4 w-full">
              <div
                {...getRootProps()}
                className="h-60 w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 bg-white p-4 rounded-xl text-center cursor-default"
              >
                <input {...getInputProps()} />
                <svg
                  className="w-10 h-10 text-gray-400 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  ></path>
                </svg>
                {dept ? (
                  <p className="text-lg font-semibold text-[#1F2937]">
                    {/* V2: updated text to "Drop files or folders here" once folder upload is re-enabled */}
                    Drop files here
                  </p>
                ) : (
                  <p className="text-lg font-semibold text-[#1F2937]">
                    {/* V2: updated text to "Drop files or folders here" once folder upload is re-enabled */}
                    Drop files or folders here
                  </p>
                )}
                <p className="text-sm text-gray-400 my-2">or</p>
                <div className="flex gap-4 mt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      open();
                    }}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-[#374151] hover:bg-gray-50 transition-colors bg-white shadow-sm"
                  >
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      ></path>
                    </svg>
                    Select files
                  </button>
                  {/* V2: re-enable "Select folder" button and folder input below */}
                  {dept ? null : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (folderInputRef.current)
                          folderInputRef.current.click();
                      }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-[#374151] hover:bg-gray-50 transition-colors bg-white shadow-sm"
                    >
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                        ></path>
                      </svg>
                      Select folder
                    </button>
                  )}
                  <input
                    type="file"
                    webkitdirectory="true"
                    directory=""
                    multiple
                    onChange={handleFolderUpload}
                    className="hidden"
                    ref={folderInputRef}
                  />
                </div>
              </div>
            </div>
          )}

          {uploadMode === "file" && (
            <div className="my-4 w-[486px]">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-gray-700">Selected Files</h5>
                <button
                  onClick={() => setUploadMode("initial")}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Change Selection
                </button>
              </div>

              {droppedFiles.length > 0 && (
                <div>
                  {droppedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex flex-row justify-between items-center my-2"
                    >
                      <span className="flex flex-row items-center gap-2">
                        <img src={FileIcon} alt="." />
                        <p className="text-sm font-bold">{file.name}</p>
                      </span>
                      <p className="text-sm">{formatFileSize(file.size)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* V2: folder upload UI — re-enable when folder support launches */}
          {uploadMode === "folder" && (
            <div className="my-4 w-[486px]">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-gray-700">Selected Folder</h5>
                <button
                  onClick={() => setUploadMode("initial")}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Change Selection
                </button>
              </div>

              {folderFiles.length > 0 && (
                <div className="text-sm my-2 text-center text-gray-600">
                  {folderFiles.length} files selected
                </div>
              )}

              <div className="my-2 w-full p-1">
                <p className="py-2 text-sm font-semibold text-gray-700">
                  Folder Name :
                </p>
                <TextField
                  variant="outlined"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  size="small"
                  fullWidth
                />

                {/* <span>
                  <p className="text-gray-700 my-2 text-sm font-semibold">
                    Folder Color
                  </p>
                  <Chrome
                    color={hex}
                    style={{
                      width: "100%",
                      margin: "auto",
                      boxShadow: "none",
                      border: "1px solid #ddd",
                    }}
                    placement={GithubPlacement.Right}
                    onChange={(color) => setHex(color.hexa)}
                  />
                </span> */}
              </div>
            </div>
          )}

          {dept ? null : (
            <div className="grid grid-cols-3 gap-2 my-4">
              {depts.map((dept, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: dept.metadata.bg,
                    border:
                      selectedDeptIndex === index
                        ? "2px solid black"
                        : "1px solid silver",
                  }}
                  // className={`flex justify-center items-center p-2 rounded-lg cursor-pointer text-sm`}
                  className="flex justify-center items-center h-14 px-2 rounded-lg cursor-pointer text-sm text-center break-words"
                  onClick={() =>
                    handleDepartmentClick(index, dept.id, dept.metadata.bg)
                  }
                >
                  {dept.name.replace("_", " ")}
                </div>
              ))}
            </div>
          )}

          {uploadProgress > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <BorderLinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, uploadProgress))}
                  />
                </div>
                <span className="text-sm font-semibold tabular-nums w-12 text-right">
                  {Math.round(Math.min(100, Math.max(0, uploadProgress)))}%
                </span>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "10px" }}>
          <div className="flex justify-between w-full px-2">
            <button
              className="px-3 py-1.5 rounded-lg shadow-sm border border-gray-300 hover:bg-gray-100 text-sm font-semibold"
              onClick={closeDialog}
            >
              Cancel
            </button>
            <button
              className={`px-3 py-1.5 rounded-lg shadow-sm border ${
                isUploadDisabled
                  ? "border-gray-300 text-gray-300 cursor-not-allowed"
                  : uploadProgress > 0
                    ? "border-gray-500 text-gray-500 hover:bg-gray-200 cursor-progress"
                    : "border-[#5E5ADB] text-[#5E5ADB] hover:bg-blue-100"
              } text-sm font-semibold`}
              onClick={handleFinalUpload}
              disabled={isUploadDisabled}
            >
              Upload
            </button>
          </div>
        </DialogActions>

        {uploadProgress > 90 && (
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
          >
            <MuiAlert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </MuiAlert>
          </Snackbar>
        )}
      </Dialog>
    </div>
  );
};

export default UnifiedUpload;
